import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  assessmentData: {
    questions: [],
    questionPath: null,
    successMessage: null,
    error: null,
  },
  formData: null,
  loading: false,
  error: null,
  successMessage: null,
  success: false,
  // Phase 2: Blueprint & category state
  blueprints: [],
  blueprintsLoading: false,
  blueprintsError: null,
  selectedBlueprint: null,
  selectedCategory: 'UNCATEGORIZED',
  // Dynamic category dropdown state
  availableCategories: [],
  categoriesLoading: false,
  // Dynamic question type picker state
  applicableTypes: [],
  typesLoading: false,
};

const assessmentCreateSlice = createSlice({
  name: 'assessment',
  initialState,
  reducers: {
    createAssessmentStart: (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    },
    createAssessmentSuccess: (state, action) => {
      state.loading = false;
      state.assessmentData = action.payload;
      state.successMessage = 'Assessment created successfully!';
      state.error = null;
      state.success = true;
    },
    createAssessmentFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.successMessage = null;
    },
    saveFormData: (state, action) => {
      // Action to save formData for future use
      state.formData = action.payload;
    },
    resetAssessmentState: (state) => {
      state.assessmentData = null;
      state.loading = false;
      state.error = null;
      state.successMessage = null;
      state.blueprints = [];
      state.blueprintsLoading = false;
      state.blueprintsError = null;
      state.selectedBlueprint = null;
      state.selectedCategory = 'UNCATEGORIZED';
      state.availableCategories = [];
      state.categoriesLoading = false;
      state.applicableTypes = [];
      state.typesLoading = false;
    },
    // Phase 2: Blueprint reducers
    setBlueprintsLoading: (state, action) => {
      state.blueprintsLoading = action.payload;
      if (action.payload) state.blueprintsError = null;
    },
    setBlueprintsData: (state, action) => {
      state.blueprints = action.payload;
      state.blueprintsLoading = false;
      state.blueprintsError = null;
    },
    setBlueprintsError: (state, action) => {
      state.blueprintsError = action.payload;
      state.blueprintsLoading = false;
    },
    setSelectedBlueprint: (state, action) => {
      state.selectedBlueprint = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    // Dynamic category dropdown reducers
    setCategoriesLoading: (state, action) => {
      state.categoriesLoading = action.payload;
    },
    setAvailableCategories: (state, action) => {
      state.availableCategories = action.payload;
      state.categoriesLoading = false;
    },
    // Dynamic question type picker reducers
    setTypesLoading: (state, action) => {
      state.typesLoading = action.payload;
    },
    setApplicableTypes: (state, action) => {
      state.applicableTypes = action.payload;
      state.typesLoading = false;
    },
    // Replace the editable list of paper-level instructions on the
    // QP-acceptance step. Source pre-fills from BlueprintTemplate
    // (createAssessmentSuccess); teacher edits route through here and
    // are sent back on finalize.
    updateGeneralInstructions: (state, action) => {
      if (state.assessmentData) {
        state.assessmentData.generalInstructions = Array.isArray(action.payload)
          ? action.payload
          : [];
      }
    },
    // Keep the authoritative paper total in Redux in sync with the teacher's
    // inline edit on /confirmquestions. Without this, the edit lived only in
    // ConfirmQuestions local state, so a remount re-initialised totalMarks from
    // the (stale) assessmentData and the entered number vanished from the UI —
    // even though it was persisted server-side. Sent back on finalize.
    updateTotalMarks: (state, action) => {
      if (state.assessmentData) {
        const next = Number(action.payload);
        state.assessmentData.totalMarks = Number.isFinite(next) ? next : 0;
      }
    },
  },
});

export const {
  createAssessmentStart,
  createAssessmentSuccess,
  createAssessmentFailure,
  saveFormData,
  resetAssessmentState,
  setBlueprintsLoading,
  setBlueprintsData,
  setBlueprintsError,
  setSelectedBlueprint,
  setSelectedCategory,
  setCategoriesLoading,
  setAvailableCategories,
  setTypesLoading,
  setApplicableTypes,
  updateGeneralInstructions,
  updateTotalMarks,
} = assessmentCreateSlice.actions;

export default assessmentCreateSlice.reducer;
